import { useEffect, useRef, useState } from "react";
import { useInterval } from "usehooks-ts";
import {
  ListWorkbenchInstancesResponseBodyItem,
  WorkbenchInstanceState,
} from "common";

interface UseWorkbenchTimeoutAlertProps {
  workbenchInstances?: ListWorkbenchInstancesResponseBodyItem[];
  enabled?: boolean;
  alertThresholdMinutes?: number;
}

/**
 * useWorkbenchTimeoutAlert hook
 *
 * This hook monitors workbench instances and alerts the user when any instance
 * has less than the specified threshold remaining before timeout expires.
 *
 * Features:
 * - Shows browser notification when tab is not active
 * - Flashes page title to grab attention
 * - Plays audio alert (optional)
 * - Automatically focuses the tab when notification is clicked
 *
 * @param workbenchInstances - Array of workbench instances to monitor
 * @param enabled - Whether the alert system is enabled (default: true)
 * @param alertThresholdMinutes - Minutes before expiry to trigger alert (default: 5)
 */
export const useWorkbenchTimeoutAlert = ({
  workbenchInstances = [],
  enabled = true,
  alertThresholdMinutes = 5,
}: UseWorkbenchTimeoutAlertProps) => {
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>("default");
  const [alertedInstances, setAlertedInstances] = useState<Set<number>>(
    new Set()
  );
  const originalTitle = useRef<string>(document.title);
  const titleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isTabVisible, setIsTabVisible] = useState<boolean>(!document.hidden);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && enabled) {
      setNotificationPermission(Notification.permission);

      if (Notification.permission === "default") {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      }
    }
  }, [enabled]);

  // Track page visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(!document.hidden);

      // Stop title flashing when tab becomes visible
      if (!document.hidden && titleIntervalRef.current) {
        clearInterval(titleIntervalRef.current);
        titleIntervalRef.current = null;
        document.title = originalTitle.current;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (titleIntervalRef.current) {
        clearInterval(titleIntervalRef.current);
        titleIntervalRef.current = null;
      }
    };
  }, []);

  // Flash page title
  const flashTitle = (message: string) => {
    if (titleIntervalRef.current) return; // Already flashing

    let isOriginal = true;
    titleIntervalRef.current = setInterval(() => {
      document.title = isOriginal ? message : originalTitle.current;
      isOriginal = !isOriginal;
    }, 1000);
  };

  // Show browser notification
  const showNotification = (
    instance: ListWorkbenchInstancesResponseBodyItem,
    timeRemaining: string
  ) => {
    if (notificationPermission !== "granted") return;

    const notification = new Notification("⚠️ Workbench Timeout Alert", {
      body: `${instance.workbench}${instance.name ? ` (${instance.name})` : ""} will expire in ${timeRemaining}!\n\n👆 Click this notification to focus the tab and extend timeout.`,
      icon: "/favicon.ico",
      tag: `workbench-timeout-${instance.id}`,
      requireInteraction: true,
      silent: false,
    });

    notification.onclick = () => {
      console.log("🖱️ Notification clicked - focusing window");

      // Try multiple methods to focus the window/tab
      if (window.parent) {
        window.parent.focus();
      }
      window.focus();

      if (window.opener) {
        window.opener.focus();
      }

      notification.close();

      // Navigate to workbench management page if not already there
      const currentPath = window.location.pathname;
      if (!currentPath.includes("/workbenchs/manage-status")) {
        window.location.href = "/workbenchs/manage-status";
      }
    };
  };

  // Check instances and alert if needed
  const checkInstances = () => {
    if (!enabled || !workbenchInstances.length) return;

    const now = new Date().getTime();
    const ALERT_THRESHOLD = alertThresholdMinutes * 60 * 1000;
    let shouldAlert = false;
    let alertMessage = "";

    workbenchInstances.forEach(instance => {
      // Only check running instances with valid timeout
      if (
        instance.status !== WorkbenchInstanceState.Running ||
        !instance.timeout?.expiresAt
      ) {
        return;
      }

      const expiresAt = new Date(instance.timeout.expiresAt).getTime();
      const timeRemaining = expiresAt - now;

      // Check if instance is within alert threshold and not already alerted
      if (timeRemaining > 0 && timeRemaining <= ALERT_THRESHOLD) {
        if (!alertedInstances.has(instance.id)) {
          const minutes = Math.floor(timeRemaining / (1000 * 60));
          const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
          const timeStr =
            minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

          // Show notification only if tab is not visible
          if (!isTabVisible) {
            showNotification(instance, timeStr);
          }

          // Mark as alerted
          setAlertedInstances(prev => new Set(prev).add(instance.id));
          shouldAlert = true;
          alertMessage = `⚠️ Workbench expiring in ${timeStr}!`;
        }
      } else if (timeRemaining > ALERT_THRESHOLD) {
        setAlertedInstances(prev => {
          if (!prev.has(instance.id)) return prev;
          const newSet = new Set(prev);
          newSet.delete(instance.id);
          return newSet;
        });
      }
    });

    // Flash title if alert is needed and tab is not visible
    if (shouldAlert && !isTabVisible) {
      flashTitle(alertMessage);
    }
  };

  // Check every 10 seconds
  useInterval(() => {
    if (enabled) {
      checkInstances();
    }
  }, 10000);

  // Also check immediately when instances change
  useEffect(() => {
    if (enabled) {
      checkInstances();
    }
  }, [workbenchInstances, enabled, alertThresholdMinutes]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (titleIntervalRef.current) {
        clearInterval(titleIntervalRef.current);
        document.title = originalTitle.current;
      }
    };
  }, []);

  return {
    notificationPermission,
    isTabVisible,
    alertedInstances: Array.from(alertedInstances),
  };
};
